import { useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color, FontSize } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import { TextAlign } from "@tiptap/extension-text-align";
import { Image } from "@tiptap/extension-image";
import { TableKit } from "@tiptap/extension-table";
import { Youtube } from "@tiptap/extension-youtube";
import { RichTextToolbar } from "@/components/RichTextToolbar";
import { HTML_RE } from "@/components/RichText";

const EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    link: { openOnClick: false, autolink: true, HTMLAttributes: { target: "_blank" } },
  }),
  TextStyle,
  Color,
  FontSize,
  Highlight.configure({ multicolor: true }),
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  Image.configure({ inline: false }),
  TableKit.configure({ table: { resizable: false } }),
  Youtube.configure({ nocookie: false, width: 640, height: 360, modestBranding: true }),
];

const toEditorHtml = (v) => {
  if (!v) return "";
  if (HTML_RE.test(v)) return v;
  const esc = v.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.split(/\n{2,}/).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
};

export const RichTextEditor = ({ value, onChange, minHeight = 90, testId = "rich-editor", placeholder }) => {
  const lastEmitted = useRef(value);
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: toEditorHtml(value),
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: "rich-content outline-none px-3 py-2", style: `min-height:${minHeight}px`, "data-testid": `${testId}-area`, "data-placeholder": placeholder || "" } },
    onUpdate: ({ editor: ed }) => {
      const html = ed.isEmpty ? "" : ed.getHTML().replace(/(<p><\/p>)+$/, "");
      lastEmitted.current = html;
      onChange(html);
    },
  });

  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    lastEmitted.current = value;
    editor.commands.setContent(toEditorHtml(value), { emitUpdate: false });
  }, [value, editor]);

  return (
    <div className="rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring/40 transition-shadow" data-testid={testId}>
      <RichTextToolbar editor={editor} testId={testId} />
      <EditorContent editor={editor} />
    </div>
  );
};
